import type { ColumnSizingState, Table } from "@tanstack/react-table";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { MIN_COLUMN_WIDTH } from "./constants";

interface UseColumnSizingOptions<T> {
  tableWidth: number | null;
  columnVisibility: Record<string, boolean>;
  visibleColumnIds: string[];
  getTable: () => Table<T> | null;
  horizontalOverflow?: boolean;
}

interface UseColumnSizingReturn {
  columnSizing: ColumnSizingState;
  handleColumnSizingChange: (
    updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState),
  ) => void;
  /**
   * Grow a single column by up to `extraWidth` pixels to fit its content.
   * In fixed-width mode the space is pulled from other columns (down to MIN_COLUMN_WIDTH),
   * so the column may not fully fit its content when space runs out. In horizontal-overflow
   * mode the table simply grows wider.
   */
  maximizeColumn: (colId: string, extraWidth: number) => void;
  /**
   * Grow multiple columns by their respective `extraWidth` pixels to fit their content.
   * Intended for horizontal-overflow mode, where every column can grow at once and the
   * table scrolls; other columns are left untouched.
   */
  maximizeColumns: (extraWidthByColId: Record<string, number>) => void;
}

/**
 * Manages column sizing state for DataTable.
 * Handles:
 * - Initial distribution of widths based on preset widths and tableWidth
 * - Adjacent column transfer during resize operations
 * - Minimum width enforcement
 */
export function useColumnSizing<T>({
  tableWidth,
  columnVisibility,
  visibleColumnIds,
  getTable,
  horizontalOverflow,
}: UseColumnSizingOptions<T>): UseColumnSizingReturn {
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const prevColumnSizingRef = useRef<ColumnSizingState>({});

  const handleColumnSizingChange = useCallback(
    (updaterOrValue: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      const prevSizing = prevColumnSizingRef.current;

      // Don't process sizing changes until useLayoutEffect has established initial sizing
      if (Object.keys(prevSizing).length === 0) {
        return;
      }

      const newSizing = typeof updaterOrValue === "function" ? updaterOrValue(columnSizing) : { ...updaterOrValue };
      const table = getTable();

      const getColSize = (colId: string) => prevSizing[colId] ?? table?.getColumn(colId)?.getSize() ?? 0;

      // Find which column was resized and calculate the delta
      let resizedColId: string | undefined;
      let delta = 0;
      for (const colId of Object.keys(newSizing)) {
        const newSize = newSizing[colId];
        const prevSize = getColSize(colId);
        if (newSize !== prevSize && newSize !== undefined) {
          resizedColId = colId;
          delta = newSize - prevSize;
          break;
        }
      }

      if (resizedColId && delta !== 0 && table) {
        if (horizontalOverflow) {
          // Horizontal overflow mode: resize only the dragged column, changing total table width
          const currentSize = getColSize(resizedColId);
          if (delta > 0) {
            newSizing[resizedColId] = currentSize + delta;
          } else {
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
          }
        } else {
          // Fixed-width mode: transfer delta to adjacent column to preserve total width
          const colIndex = visibleColumnIds.indexOf(resizedColId);
          const nextColId = visibleColumnIds[colIndex + 1];

          if (delta > 0) {
            if (!nextColId) {
              return;
            }
            const nextColCurrentSize = getColSize(nextColId);
            const availableSpace = nextColCurrentSize - MIN_COLUMN_WIDTH;
            if (availableSpace <= 0) {
              return;
            }
            const cappedDelta = Math.min(delta, availableSpace);
            newSizing[resizedColId] = getColSize(resizedColId) + cappedDelta;
            newSizing[nextColId] = nextColCurrentSize - cappedDelta;
          } else {
            const currentSize = getColSize(resizedColId);
            const minAllowedDelta = MIN_COLUMN_WIDTH - currentSize;
            const cappedDelta = Math.max(delta, minAllowedDelta);
            if (cappedDelta >= 0) {
              return;
            }
            newSizing[resizedColId] = currentSize + cappedDelta;
            if (nextColId) {
              newSizing[nextColId] = getColSize(nextColId) - cappedDelta;
            }
          }
        }
      }

      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [columnSizing, visibleColumnIds, getTable, horizontalOverflow],
  );

  // Reads the current width of every visible column, preferring the last committed sizing and
  // falling back to react-table's computed size before the initial layout effect has run.
  const getCurrentSizing = useCallback((): ColumnSizingState => {
    const table = getTable();
    const sizing: ColumnSizingState = {};
    for (const col of table?.getVisibleLeafColumns() ?? []) {
      sizing[col.id] = prevColumnSizingRef.current[col.id] ?? col.getSize();
    }
    return sizing;
  }, [getTable]);

  const maximizeColumn = useCallback(
    (colId: string, extraWidth: number) => {
      if (extraWidth <= 0) {
        return;
      }
      const current = getCurrentSizing();
      if (current[colId] === undefined) {
        return;
      }

      const newSizing: ColumnSizingState = { ...current };

      if (horizontalOverflow) {
        // Table can grow, so just widen the target column.
        newSizing[colId] = current[colId] + extraWidth;
      } else {
        // Pull the needed space from other columns, proportional to how much each can spare
        // (never below MIN_COLUMN_WIDTH). Keeps the total width equal to the container.
        // Exclude columns that opt out of resizing so maximizing one can't shrink a locked one.
        const table = getTable();
        const others = visibleColumnIds.filter(
          (id) => id !== colId && current[id] !== undefined && table?.getColumn(id)?.getCanResize() !== false,
        );
        const available: Record<string, number> = {};
        let totalAvailable = 0;
        for (const id of others) {
          const avail = Math.max(0, current[id] - MIN_COLUMN_WIDTH);
          available[id] = avail;
          totalAvailable += avail;
        }
        if (totalAvailable <= 0) {
          return;
        }
        const grant = Math.min(extraWidth, totalAvailable);

        let distributed = 0;
        for (const id of others) {
          if (distributed >= grant) {
            break;
          }
          const proportional = Math.round((available[id] / totalAvailable) * grant);
          const share = Math.min(proportional, available[id], grant - distributed);
          newSizing[id] = current[id] - share;
          distributed += share;
        }
        // Assign any rounding remainder to columns that still have room to spare.
        if (distributed < grant) {
          for (const id of others) {
            if (distributed >= grant) {
              break;
            }
            const remaining = Math.min(newSizing[id] - MIN_COLUMN_WIDTH, grant - distributed);
            if (remaining > 0) {
              newSizing[id] -= remaining;
              distributed += remaining;
            }
          }
        }
        newSizing[colId] = current[colId] + distributed;
      }

      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [getCurrentSizing, getTable, horizontalOverflow, visibleColumnIds],
  );

  const maximizeColumns = useCallback(
    (extraWidthByColId: Record<string, number>) => {
      const current = getCurrentSizing();
      const newSizing: ColumnSizingState = { ...current };
      let changed = false;
      for (const id of visibleColumnIds) {
        const extra = extraWidthByColId[id] ?? 0;
        if (extra > 0 && current[id] !== undefined) {
          newSizing[id] = current[id] + extra;
          changed = true;
        }
      }
      if (!changed) {
        return;
      }
      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    },
    [getCurrentSizing, visibleColumnIds],
  );

  // Store getTable in a ref to avoid re-running effect when function reference changes
  const getTableRef = useRef(getTable);
  getTableRef.current = getTable;

  // Serialize columnVisibility for stable dependency comparison
  const columnVisibilityKey = JSON.stringify(columnVisibility);

  useLayoutEffect(() => {
    const table = getTableRef.current();
    const visibleCols = table?.getVisibleLeafColumns();
    if (!tableWidth || !visibleCols?.length) {
      return;
    }

    const newSizing: ColumnSizingState = {};

    // Collect preset widths (from width prop) and columns needing distribution
    let presetTotal = 0;
    const colsNeedingDistribution: typeof visibleCols = [];

    for (const col of visibleCols) {
      const presetSize = col.columnDef.size;
      if (presetSize !== undefined) {
        newSizing[col.id] = presetSize;
        presetTotal += presetSize;
      } else {
        colsNeedingDistribution.push(col);
      }
    }

    // Distribute remaining width equally to columns without presets
    if (colsNeedingDistribution.length > 0) {
      const remaining = tableWidth - presetTotal;
      const widthPerCol = Math.max(MIN_COLUMN_WIDTH, remaining / colsNeedingDistribution.length);
      for (const col of colsNeedingDistribution) {
        newSizing[col.id] = widthPerCol;
      }
    }

    // If total doesn't match tableWidth, distribute difference equally among ALL columns.
    // In horizontal overflow mode, only equalize when columns under-fill the table width
    // (don't shrink columns that the user has expanded beyond the container).
    const total = Object.values(newSizing).reduce((sum, size) => sum + size, 0);
    if (Math.abs(total - tableWidth) > 1 && (!horizontalOverflow || total < tableWidth)) {
      const extraPerCol = (tableWidth - total) / visibleCols.length;
      for (const col of visibleCols) {
        newSizing[col.id] = Math.max(MIN_COLUMN_WIDTH, (newSizing[col.id] ?? 0) + extraPerCol);
      }
    }

    // Only update if sizing actually changed
    const sizingChanged = visibleCols.some((col) => {
      const newSize = newSizing[col.id];
      const oldSize = columnSizing[col.id];
      return oldSize === undefined || Math.abs(newSize - oldSize) > 1;
    });

    if (sizingChanged) {
      prevColumnSizingRef.current = newSizing;
      setColumnSizing(newSizing);
    }
  }, [tableWidth, columnVisibilityKey]);

  return {
    columnSizing,
    handleColumnSizingChange,
    maximizeColumn,
    maximizeColumns,
  };
}
